import { useEffect, useRef, useState } from 'react'
import { Volume2, VolumeX } from 'lucide-react'

const chapters = ['seed', 'roots', 'branches', 'canopy', 'harvest']

export default function AudioToggle() {
  const enabledRef = useRef(false)
  const mutedRef = useRef(true)
  const ambientRef = useRef(null)
  const voicesRef = useRef({})
  const currentVoiceRef = useRef(null)
  const rafRef = useRef(null)
  const [muted, setMuted] = useState(true)

  useEffect(() => {
    const ambient = new Audio('/audio/thoughtseed-ambient.mp3')
    ambient.loop = true
    ambient.volume = 0
    ambient.crossOrigin = 'anonymous'
    ambientRef.current = ambient

    chapters.forEach((chapter) => {
      const a = new Audio(`/audio/voiceover-${chapter}.mp3`)
      a.volume = 0
      voicesRef.current[chapter] = a
    })

    let lastChapter = null

    const tick = () => {
      if (!enabledRef.current) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
      const progress = scrollHeight > 0 ? scrollTop / scrollHeight : 0

      let active
      if (progress < 0.15) active = 'seed'
      else if (progress < 0.35) active = 'roots'
      else if (progress < 0.55) active = 'branches'
      else if (progress < 0.78) active = 'canopy'
      else active = 'harvest'

      if (lastChapter !== active) {
        if (currentVoiceRef.current) {
          currentVoiceRef.current.pause()
          currentVoiceRef.current.currentTime = 0
        }
        const next = voicesRef.current[active]
        if (next) {
          next.volume = mutedRef.current ? 0 : 0.85
          next.play().catch(() => {})
          currentVoiceRef.current = next
        }
        lastChapter = active
      }

      if (ambientRef.current) {
        ambientRef.current.volume = mutedRef.current ? 0 : 0.22
        if (ambientRef.current.paused && !mutedRef.current) {
          ambientRef.current.play().catch(() => {})
        } else if (!ambientRef.current.paused && mutedRef.current) {
          ambientRef.current.pause()
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(rafRef.current)
      ambient.pause()
      Object.values(voicesRef.current).forEach((a) => a.pause())
    }
  }, [])

  const toggle = () => {
    if (!enabledRef.current) {
      enabledRef.current = true
      mutedRef.current = false
      ambientRef.current?.play().catch(() => {})
    } else {
      mutedRef.current = !mutedRef.current
      if (currentVoiceRef.current) {
        currentVoiceRef.current.volume = mutedRef.current ? 0 : 0.85
      }
      if (ambientRef.current) {
        ambientRef.current.volume = mutedRef.current ? 0 : 0.22
        if (mutedRef.current) ambientRef.current.pause()
        else ambientRef.current.play().catch(() => {})
      }
    }
    setMuted(mutedRef.current)
  }

  return (
    <button
      onClick={toggle}
      className="fixed bottom-6 right-6 z-[60] w-12 h-12 flex items-center justify-center rounded-[14px] border border-white/10 bg-[#020b1f]/80 backdrop-blur-md hover:bg-[#041536]/90 active:scale-95 transition-all"
      aria-label={muted ? 'Enable sound' : 'Mute sound'}
    >
      {muted ? (
        <VolumeX className="w-5 h-5 text-white/70" />
      ) : (
        <Volume2 className="w-5 h-5 text-white" />
      )}
    </button>
  )
}
